import React from 'react';
import type { AccessArea } from '../../access/areas';
import {
  SpaceDashboardOutlined as DashboardIcon,
  TimelineOutlined as OperationsIcon,
  FolderCopyOutlined as FilesIcon,
  AssignmentOutlined as TaskIcon,
  PeopleAltOutlined as PeopleIcon,
  LocalShippingOutlined as FleetIcon,
  HowToRegOutlined as HiringIcon,
  BusinessOutlined as BusinessIcon,
  SettingsOutlined as SettingsIcon,
  PaymentsOutlined as PayrollIcon,
  AssessmentOutlined as ReportsIcon,
  CalendarMonthOutlined as ScheduleIcon,
} from '@mui/icons-material';

// One list of sections, in one place. The sidebar, the breadcrumb and the
// document title all read from this, so a route can no longer be present in
// the nav and unnamed in the breadcrumb (or vice versa).

export interface NavSection {
  /** Route prefix. Active state is a prefix match, not equality — see below. */
  to: string;
  label: string;
  icon: React.ReactNode;
  permission?: string;
  /** Shown when the person has any of these access-matrix areas. */
  areas?: AccessArea[];
  /** SA-only regardless of permissions. */
  systemAdminOnly?: boolean;
  /**
   * Hidden from the rail but still routable. Tasks is a "coming soon" stub;
   * a permanent tab that leads to an apology is worse than no tab.
   */
  hidden?: boolean;
  /**
   * Extra route prefixes that belong to this section without being its
   * root. /coverage is a Dashboard screen reached from the Dashboard
   * panel; without this the rail goes dark there and the breadcrumb has
   * nothing to name.
   */
  alsoMatches?: string[];
}

export const NAV_SECTIONS: NavSection[] = [
  { to: '/dashboard', label: 'Dashboard', icon: <DashboardIcon />, alsoMatches: ['/coverage'] },
  { to: '/operations', label: 'Operations', icon: <OperationsIcon />, areas: ['operations'] },
  // Who works which days against the volume expected. Part of running the
  // operation, so it sits with it, and the same people see it.
  { to: '/schedule', label: 'Schedule', icon: <ScheduleIcon />, areas: ['operations'] },
  { to: '/files', label: 'Files', icon: <FilesIcon />, areas: ['files'] },
  { to: '/tasks', label: 'Tasks', icon: <TaskIcon />, areas: ['tasks'], hidden: true },
  { to: '/users', label: 'Users', icon: <PeopleIcon />, areas: ['people'] },
  // The pipeline that produces the people in Users.
  //
  // Gated like the vault and the fleet, NOT on 'users.view'. The first
  // version used that and it hid the section from every BC — who is the
  // role that actually runs hiring: they send the outreach, hold the
  // interview, book the road test, and are the `responsibleRole` on most
  // of the checkpoint catalog. Same reasoning as Fleet: a BC runs their
  // area and needs the list.
  //
  // The real gate is the API, which refuses Drivers and Maintainers
  // outright and scopes a BC to their own service area.
  { to: '/hiring', label: 'Hiring', icon: <HiringIcon />, areas: ['hiring'] },
  // The asset record. The vault's vehicle roster answers "whose paperwork
  // is late"; this answers "what do we own, and is it on the road". Gated
  // like the vault rather than on 'users.view' — a BC runs the trucks in
  // their area and needs the list.
  { to: '/fleet', label: 'Fleet', icon: <FleetIcon />, areas: ['fleet'] },
  // What everyone is paid, from the week's FedEx statement. Owner and
  // operations admin only: a BC runs an area, not the payroll, and the
  // API router refuses everyone else regardless of what the rail shows.
  { to: '/payroll', label: 'Payroll', icon: <PayrollIcon />, areas: ['payroll'] },
  // Every report together: payroll and revenue, weekly volume, hiring.
  // Each still reads its own data; the panel inside filters by permission.
  { to: '/reports', label: 'Reports', icon: <ReportsIcon />, areas: ['reports', 'hiring'] },
  { to: '/companies', label: 'Companies', icon: <BusinessIcon />, systemAdminOnly: true },
  { to: '/settings', label: 'Settings', icon: <SettingsIcon /> },
];

/**
 * Which section a path belongs to.
 *
 * The old sidebar compared `location.pathname === link.to`, so the moment
 * you opened a document (/files/document/:id) or a workflow run
 * (/operations/:id) every item in the nav went dark and the app stopped
 * telling you where you were. Longest prefix wins so /files never claims a
 * hypothetical /files-archive.
 */
export function sectionForPath(pathname: string): NavSection | undefined {
  const owns = (prefix: string) => pathname === prefix || pathname.startsWith(`${prefix}/`);
  return NAV_SECTIONS
    .filter((s) => owns(s.to) || (s.alsoMatches ?? []).some(owns))
    .sort((a, b) => b.to.length - a.to.length)[0];
}
